import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, Save, AlertCircle, ChevronDown, Check } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { API_ENDPOINTS, API_BASE_URL } from '../../config/api';
import { AppointmentUpdate, AppointmentType, AppointmentStatus, Appointment } from '../../types/appointment';
import { fetchCachedTeamMembers, type TeamMember } from '../../utils/teamMembersCache';

// Status labels for dropdown
const STATUS_LABELS: Record<AppointmentStatus, string> = {
  [AppointmentStatus.PENDING]: 'Pending',
  [AppointmentStatus.SCHEDULED]: 'Scheduled',
  [AppointmentStatus.CONFIRMED]: 'Confirmed',
  [AppointmentStatus.IN_PROGRESS]: 'In Progress',
  [AppointmentStatus.COMPLETE]: 'Complete',
  [AppointmentStatus.CANCELLED]: 'Cancelled',
  [AppointmentStatus.NO_SHOW]: 'No Show',
  [AppointmentStatus.ERROR]: 'Error',
  [AppointmentStatus.COMPLETED]: 'Completed'
};

const ALL_STATUSES = [
  AppointmentStatus.PENDING,
  AppointmentStatus.SCHEDULED,
  AppointmentStatus.CONFIRMED,
  AppointmentStatus.IN_PROGRESS,
  AppointmentStatus.COMPLETE,
  AppointmentStatus.CANCELLED,
  AppointmentStatus.NO_SHOW
];

// Appointment type labels for dropdown
const TYPE_LABELS: Record<AppointmentType, string> = {
  [AppointmentType.CHECKUP]: 'Check-up',
  [AppointmentType.SURGERY]: 'Surgery',
  [AppointmentType.EMERGENCY]: 'Emergency',
  [AppointmentType.CONSULTATION]: 'Consultation'
};

const ALL_TYPES = [
  AppointmentType.CHECKUP,
  AppointmentType.SURGERY,
  AppointmentType.EMERGENCY,
  AppointmentType.CONSULTATION
];

// Duration options for dropdown
const DURATION_OPTIONS = [
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 45, label: '45 minutes' },
  { value: 60, label: '60 minutes' },
  { value: 90, label: '90 minutes' }
];

interface PetOwner {
  uuid: string;
  full_name: string;
  email?: string;
  phone?: string;
}

interface Pet {
  id: string;
  name: string;
  species: string;
  breed?: string;
}

interface AppointmentEditFormProps {
  appointmentId: string;
  onClose: () => void;
  onSuccess?: (appointment?: any) => void;
}

const AppointmentEditForm: React.FC<AppointmentEditFormProps> = ({ appointmentId, onClose, onSuccess }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  const [appointment, setAppointment] = useState<Appointment | null>(null);
  const [formData, setFormData] = useState<AppointmentUpdate>({
    assigned_vet_user_id: '',
    appointment_date: '',
    duration_minutes: 30,
    appointment_type: AppointmentType.CHECKUP,
    status: AppointmentStatus.SCHEDULED,
    title: '',
    description: '',
    notes: '',
    pet_ids: []
  });

  const [petOwners, setPetOwners] = useState<PetOwner[]>([]);
  const [pets, setPets] = useState<Pet[]>([]);
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState('');
  
  // Status dropdown state
  const [isStatusDropdownOpen, setIsStatusDropdownOpen] = useState(false);
  const statusButtonRef = useRef<HTMLButtonElement>(null);
  const [statusDropdownPosition, setStatusDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Type dropdown state
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const typeButtonRef = useRef<HTMLButtonElement>(null);
  const [typeDropdownPosition, setTypeDropdownPosition] = useState({ top: 0, left: 0 });
  
  // Duration dropdown state
  const [isDurationDropdownOpen, setIsDurationDropdownOpen] = useState(false);
  const durationButtonRef = useRef<HTMLButtonElement>(null);
  const [durationDropdownPosition, setDurationDropdownPosition] = useState({ top: 0, left: 0 });

  useEffect(() => {
    fetchAppointment();
  }, [appointmentId]);

  useEffect(() => {
    if (selectedOwnerId) {
      loadPetsForOwner(selectedOwnerId);
    } else {
      setPets([]);
    }
  }, [selectedOwnerId]);

  const fetchAppointment = async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/appointments/${appointmentId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) throw new Error('Failed to fetch appointment');
      
      const data: Appointment = await response.json();
      setAppointment(data);
      
      // Convert ISO date (UTC) to local time for datetime-local input
      const localDate = new Date(data.appointment_date);
      const year = localDate.getFullYear();
      const month = String(localDate.getMonth() + 1).padStart(2, '0');
      const day = String(localDate.getDate()).padStart(2, '0');
      const hours = String(localDate.getHours()).padStart(2, '0');
      const minutes = String(localDate.getMinutes()).padStart(2, '0');
      const localDateString = `${year}-${month}-${day}T${hours}:${minutes}`;

      setFormData({
        assigned_vet_user_id: data.assigned_vet_user_id || '',
        appointment_date: localDateString,
        duration_minutes: data.duration_minutes,
        appointment_type: data.appointment_type,
        status: data.status,
        title: data.title,
        description: data.description || '',
        notes: data.notes || '',
        pet_ids: data.pets.map(p => p.id)
      });

      setSelectedOwnerId(data.pet_owner_id);

      // Load pet owners
      const petOwnersRes = await fetch(API_ENDPOINTS.PET_OWNERS.LIST);
      if (petOwnersRes.ok) {
        const petOwnersData = await petOwnersRes.json();
        setPetOwners(petOwnersData);
      }

      // Load team members (with caching)
      if (user?.practice_id) {
        const token = localStorage.getItem('access_token');
        const members = await fetchCachedTeamMembers(user.practice_id, token);
        setTeamMembers(members);
      }

      // Load pets for the owner
      if (data.pet_owner_id) {
        await loadPetsForOwner(data.pet_owner_id);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load appointment');
    } finally {
      setLoading(false);
    }
  };

  const loadPetsForOwner = async (ownerId: string) => {
    try {
      const response = await fetch(API_ENDPOINTS.PETS.BY_OWNER(ownerId));
      if (response.ok) {
        const petsData = await response.json();
        setPets(petsData);
      } else {
        setPets([]);
      }
    } catch (err) {
      console.error('Error loading pets:', err);
      setPets([]);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: name === 'duration_minutes' ? parseInt(value) || 0 : value
    }));
  };

  const handlePetSelection = (petId: string, checked: boolean) => {
    setFormData(prev => ({
      ...prev,
      pet_ids: checked 
        ? [...(prev.pet_ids || []), petId]
        : (prev.pet_ids || []).filter(id => id !== petId)
    }));
  };

  // Generate dynamic title based on pet names, appointment type, and date/time
  const generateTitle = () => {
    const selectedPets = pets.filter(p => formData.pet_ids?.includes(p.id));
    const petNames = selectedPets.map(p => p.name).join(', ') || 'Pet';
    
    const appointmentTypeLabel = formData.appointment_type 
      ? formData.appointment_type.charAt(0).toUpperCase() + formData.appointment_type.slice(1)
      : 'Appointment';
    
    let dateTimeStr = '';
    if (formData.appointment_date) {
      const date = new Date(formData.appointment_date);
      const dateStr = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      dateTimeStr = ` - ${dateStr} at ${timeStr}`;
    }
    
    return `${petNames} ${appointmentTypeLabel}${dateTimeStr}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      // Generate dynamic title and convert appointment_date to ISO format
      const appointmentData: AppointmentUpdate = {
        ...formData,
        title: generateTitle(),
        appointment_date: new Date(formData.appointment_date!).toISOString()
      };

      const token = localStorage.getItem('access_token');
      const baseURL = API_BASE_URL;

      const response = await fetch(`${baseURL}/api/v1/appointments/${appointmentId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(appointmentData)
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Failed to update appointment');
      }

      const updatedAppointment = await response.json();
      if (onSuccess) {
        onSuccess(updatedAppointment);
      }
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update appointment');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full p-6">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <h2 className="text-base font-semibold text-gray-900">Edit Appointment</h2>
        <button
          onClick={onClose}
          className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition-colors"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-6 space-y-5">
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start space-x-2">
            <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {/* Pet Owner - Display only, not editable */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pet Owner
          </label>
          <div className="px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-sm text-gray-700">
            {petOwners.find(o => o.uuid === selectedOwnerId)?.full_name || 'Loading...'}
          </div>
          <p className="text-xs text-gray-500 mt-1">Pet owner cannot be changed after creation</p>
        </div>

        {/* Pets Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Pets <span className="text-red-500">*</span>
          </label>
          {pets.length > 0 ? (
            <div className="space-y-2">
              {pets.map((pet) => (
                <label
                  key={pet.id}
                  className="flex items-center space-x-2 p-2 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={formData.pet_ids?.includes(pet.id) || false}
                    onChange={(e) => handlePetSelection(pet.id, e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-gray-900">{pet.name}</p>
                    <p className="text-xs text-gray-500">
                      {pet.species}
                      {pet.breed && ` • ${pet.breed}`}
                    </p>
                  </div>
                </label>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-500">No pets available for this owner</p>
          )}
        </div>

        {/* Date and Time */}
        <div>
          <label htmlFor="appointment_date" className="block text-sm font-medium text-gray-700 mb-1">
            Date & Time <span className="text-red-500">*</span>
          </label>
          <input
            type="datetime-local"
            id="appointment_date"
            name="appointment_date"
            value={formData.appointment_date}
            onChange={handleInputChange}
            required
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          />
        </div>

        {/* Duration */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Duration <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <button
              ref={durationButtonRef}
              type="button"
              onClick={() => {
                if (durationButtonRef.current) {
                  const rect = durationButtonRef.current.getBoundingClientRect();
                  setDurationDropdownPosition({ top: rect.bottom + 4, left: rect.left });
                }
                setIsDurationDropdownOpen(!isDurationDropdownOpen);
              }}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <span className="text-sm text-gray-900">
                {DURATION_OPTIONS.find(d => d.value === formData.duration_minutes)?.label || 'Select duration'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isDurationDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isDurationDropdownOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[600]" onClick={() => setIsDurationDropdownOpen(false)} />
                <div
                  className="fixed z-[700] w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                  style={{ top: durationDropdownPosition.top, left: durationDropdownPosition.left }}
                >
                  {DURATION_OPTIONS.map((option) => {
                    const isSelected = formData.duration_minutes === option.value;

                    return (
                      <button
                        key={option.value}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, duration_minutes: option.value }));
                          setIsDurationDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <span className="text-sm text-gray-900">{option.label}</span>
                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        {/* Appointment Type */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Type <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <button
              ref={typeButtonRef}
              type="button"
              onClick={() => {
                if (typeButtonRef.current) {
                  const rect = typeButtonRef.current.getBoundingClientRect();
                  setTypeDropdownPosition({ top: rect.bottom + 4, left: rect.left });
                }
                setIsTypeDropdownOpen(!isTypeDropdownOpen);
              }}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <span className="text-sm text-gray-900">
                {formData.appointment_type ? TYPE_LABELS[formData.appointment_type] : 'Select type'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isTypeDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isTypeDropdownOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[600]" onClick={() => setIsTypeDropdownOpen(false)} />
                <div
                  className="fixed z-[700] w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                  style={{ top: typeDropdownPosition.top, left: typeDropdownPosition.left }}
                >
                  {ALL_TYPES.map((typeOption) => {
                    const label = TYPE_LABELS[typeOption];
                    const isSelected = formData.appointment_type === typeOption;

                    return (
                      <button
                        key={typeOption}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, appointment_type: typeOption }));
                          setIsTypeDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <span className="text-sm text-gray-900">{label}</span>
                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        {/* Status */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Status <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <button
              ref={statusButtonRef}
              type="button"
              onClick={() => {
                if (statusButtonRef.current) {
                  const rect = statusButtonRef.current.getBoundingClientRect();
                  setStatusDropdownPosition({ top: rect.bottom + 4, left: rect.left });
                }
                setIsStatusDropdownOpen(!isStatusDropdownOpen);
              }}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-lg bg-white hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
            >
              <span className="text-sm text-gray-900">
                {formData.status ? STATUS_LABELS[formData.status] : 'Select status'}
              </span>
              <ChevronDown className={`w-4 h-4 text-gray-500 transition-transform ${isStatusDropdownOpen ? 'rotate-180' : ''}`} />
            </button>

            {isStatusDropdownOpen && createPortal(
              <>
                <div className="fixed inset-0 z-[600]" onClick={() => setIsStatusDropdownOpen(false)} />
                <div
                  className="fixed z-[700] w-56 bg-white rounded-lg shadow-lg border border-gray-200 py-1"
                  style={{ top: statusDropdownPosition.top, left: statusDropdownPosition.left }}
                >
                  {ALL_STATUSES.map((statusOption) => {
                    const label = STATUS_LABELS[statusOption];
                    const isSelected = formData.status === statusOption;

                    return (
                      <button
                        key={statusOption}
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, status: statusOption }));
                          setIsStatusDropdownOpen(false);
                        }}
                        className={`w-full flex items-center justify-between px-3 py-2.5 text-left hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
                      >
                        <span className="text-sm text-gray-900">{label}</span>
                        {isSelected && <Check className="w-4 h-4 text-blue-600" />}
                      </button>
                    );
                  })}
                </div>
              </>,
              document.body
            )}
          </div>
        </div>

        {/* Description */}
        <div>
          <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
            Description
          </label>
          <textarea
            id="description"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="Brief description of the appointment..."
          />
        </div>

        {/* Notes */}
        <div>
          <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-1">
            Internal Notes
          </label>
          <textarea
            id="notes"
            name="notes"
            value={formData.notes}
            onChange={handleInputChange}
            rows={3}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
            placeholder="Internal notes (not visible to pet owner)..."
          />
        </div>
      </form>

      {/* Footer */}
      <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
        <button
          type="button"
          onClick={onClose}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Cancel
        </button>
        <button
          type="submit"
          onClick={handleSubmit}
          disabled={saving}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
        >
          {saving ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              <span>Saving...</span>
            </>
          ) : (
            <>
              <Save className="w-4 h-4" />
              <span>Save Changes</span>
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default AppointmentEditForm;

